import React, { useContext } from "react";
import { Route, Routes, Navigate, useParams } from "react-router-dom";
import SjorcraftEditor from "./components/SjorcraftEditor";
import { darkTheme, defaultTheme } from "./contexts/colorContext";
import { ThemeProvider } from "@mui/material";
import Faq from "./pages/Faq";
import LoginPage from "./pages/authentication/LoginPage";
import RegisterPage from "./pages/authentication/RegisterPage";
import ForgotPasswordPage from "./pages/authentication/ForgotPasswordPage";
import ResetPasswordPage from "./pages/authentication/ResetPasswordPage";
import ProjectsOverviewPage from "./pages/projectsOverview/ProjectsOverviewPage";
import SettingsPage from "./pages/settings/SettingsPage";
import WithNavigationBar from "./layout/WithNavigationBar";
import { UserContext } from "./contexts/userContext";
import { useDarkmode } from "./hooks/useDarkmode";
import SignUpPage from "./pages/authentication/SignUpPage";
import { SnackbarProvider } from "./contexts/snackbarContext";
import { PinwheelLoader } from "./components/PinwheelLoader";
import AdminCenterPage from "./pages/adminCenter/AdminCenterPage";
import { useQuery } from "@apollo/client";
import { PROJECT_STATUS } from "./graphql/gqlProject";
import CreateProjectButton from "./pages/projectsOverview/CreateProjectButton";
import { Status, toStatus } from "./graphql/gqlTypes";
import PolesetsPage from "./pages/polesets/PolesetsPage";
import CreatePolesetButton from "./pages/polesets/CreatePolesetButton";

const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const userContext = useContext(UserContext)!;
  const { projectId } = useParams();
  const {
    data,
    loading: statusLoading,
    error,
  } = useQuery(PROJECT_STATUS, {
    variables: { projectId },
    skip: !projectId,
  });
  const currentStatus: Status = toStatus(data?.getProjectById?.status);
  if (userContext.loading || statusLoading) return <PinwheelLoader />;
  if (projectId) {
    if (currentStatus === "SHARED" || currentStatus === "PUBLIC") {
      return children;
    }
  }
  if (!userContext.user?.id) {
    return <Navigate to="/login" />;
  }
  return children;
};

const AdminOnlyRoute = ({ children }: { children: JSX.Element }) => {
  const userContext = useContext(UserContext)!;
  if (userContext.loading) return <PinwheelLoader />;
  if (!userContext.user.isAdmin) {
    return <Navigate to="/" />;
  }
  return children;
};

const App = () => {
  const { isDarkmode } = useDarkmode();

  return (
    <ThemeProvider theme={isDarkmode ? darkTheme : defaultTheme}>
      <SnackbarProvider>
        <Routes>
          <Route index element={<Navigate to="/login" />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />
          <Route path="/registration/:token" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route
            path="/reset-password/:token"
            element={<ResetPasswordPage />}
          />
          <Route
            path="/:userId/projects"
            element={
              <ProtectedRoute>
                <WithNavigationBar callToAction={<CreateProjectButton />}>
                  <ProjectsOverviewPage view="user" />
                </WithNavigationBar>
              </ProtectedRoute>
            }
          />
          <Route
            path="/:userId/projects/:projectId"
            element={
              <ProtectedRoute>
                <SjorcraftEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:userId/polesets"
            element={
              <ProtectedRoute>
                <WithNavigationBar callToAction={<CreatePolesetButton />}>
                  <PolesetsPage />
                </WithNavigationBar>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <WithNavigationBar>
                  <SettingsPage />
                </WithNavigationBar>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-center"
            element={
              <AdminOnlyRoute>
                <WithNavigationBar>
                  <AdminCenterPage />
                </WithNavigationBar>
              </AdminOnlyRoute>
            }
          />
          <Route
            path="/faq"
            element={
              <WithNavigationBar>
                <Faq />
              </WithNavigationBar>
            }
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default App;
